import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const REVIEW_API_BASE = `${API_BASE_URL}/reviews`;

export interface Review {
  id: number;
  productId: number;
  productTitle: string;
  userId: number;
  userName: string;
  title: string;
  comment: string;
  rating: number;
  verified: boolean;
  status: string;
  helpfulCount: number;
  unhelpfulCount: number;
  adminResponse?: string;
  adminResponseDate?: string;
  createdAt: string;
  updatedAt: string;
  editable: boolean;
}

export interface ReviewRequest {
  title: string;
  comment: string;
  rating: number;
}

export interface ReviewStats {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

class ReviewService {
  // Get reviews for a product (public - only approved)
  async getProductReviews(
    productId: number,
    page = 1,
    size = 10,
    sortBy = 'createdAt',
    sortDirection = 'DESC'
  ) {
    // Map frontend sort parameters to backend format
    let sort = 'newest'; // default
    if (sortBy === 'createdAt') {
      sort = sortDirection === 'ASC' ? 'oldest' : 'newest';
    } else if (sortBy === 'rating') {
      sort = sortDirection === 'ASC' ? 'lowest' : 'highest';
    } else if (sortBy === 'helpful') {
      sort = 'helpful';
    }
    
    const response = await axios.get(`${REVIEW_API_BASE}/product/${productId}`, {
      params: { page: Math.max(1, page), per_page: size, sort }
    });
    return response.data;
  }

  // Get review statistics for a product
  async getProductReviewStats(productId: number): Promise<ReviewStats> {
    const response = await axios.get(`${REVIEW_API_BASE}/product/${productId}/stats`);
    const data = response.data;
    
    // Transform snake_case backend response to camelCase frontend format
    return {
      averageRating: data.average_rating || 0,
      totalReviews: data.total_reviews || 0,
      ratingDistribution: data.rating_breakdown || {
        1: 0, 2: 0, 3: 0, 4: 0, 5: 0
      }
    };
  }

  // Get user's review for a specific product
  async getUserProductReview(productId: number): Promise<Review | null> {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${REVIEW_API_BASE}/product/${productId}/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Get user's reviews
  async getUserReviews(page = 0, size = 10) {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${REVIEW_API_BASE}/user`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { page, size }
    });
    return response.data;
  }

  // Create a new review
  async createReview(productId: number, review: ReviewRequest): Promise<Review> {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${REVIEW_API_BASE}/product/${productId}`,
      review,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  // Update a review
  async updateReview(reviewId: number, review: ReviewRequest): Promise<Review> {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${REVIEW_API_BASE}/${reviewId}`,
      review,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  // Delete a review
  async deleteReview(reviewId: number): Promise<void> {
    const token = localStorage.getItem('token');
    await axios.delete(`${REVIEW_API_BASE}/${reviewId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  // Vote review as helpful
  async voteHelpful(reviewId: number): Promise<Review> {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${REVIEW_API_BASE}/${reviewId}/helpful`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  // Vote review as unhelpful
  async voteUnhelpful(reviewId: number): Promise<Review> {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${REVIEW_API_BASE}/${reviewId}/unhelpful`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  // Get recent reviews (for homepage)
  async getRecentReviews(limit = 5): Promise<Review[]> {
    const response = await axios.get(`${REVIEW_API_BASE}/recent`, {
      params: { limit }
    });
    return response.data;
  }

  // Search reviews
  async searchReviews(query: string, page = 0, size = 10) {
    const response = await axios.get(`${REVIEW_API_BASE}/search`, {
      params: { query, page, size }
    });
    return response.data;
  }

  // Admin endpoints

  // Get all reviews for a product (including pending)
  async getAllProductReviews(productId: number, page = 0, size = 10) {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${REVIEW_API_BASE}/admin/product/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { page, size }
    });
    return response.data;
  }

  // Get reviews for moderation
  async getReviewsForModeration(page = 0, size = 10) {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/admin/reviews`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { 
        page: page + 1, // Backend uses 1-based pagination
        per_page: size,
        status: 'pending' // Only get pending reviews for moderation
      }
    });
    return response.data;
  }

  // Get flagged reviews (using same endpoint with different params)
  async getFlaggedReviews(page = 0, size = 10) {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_BASE_URL}/admin/reviews`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { 
        page: page + 1, // Backend uses 1-based pagination
        per_page: size
        // No status filter - get all reviews that could be flagged
      }
    });
    return response.data;
  }

  // Approve review
  async approveReview(reviewId: number): Promise<Review> {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${API_BASE_URL}/admin/reviews/${reviewId}/approve`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  // Reject review
  async rejectReview(reviewId: number, reason: string): Promise<Review> {
    const token = localStorage.getItem('token');
    const response = await axios.put(
      `${API_BASE_URL}/admin/reviews/${reviewId}/reject`,
      { reason },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  // Flag review
  async flagReview(reviewId: number): Promise<Review> {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${REVIEW_API_BASE}/admin/${reviewId}/flag`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  }

  // Add admin response
  async addAdminResponse(reviewId: number, response: string): Promise<Review> {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      `${REVIEW_API_BASE}/admin/${reviewId}/response`,
      null,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { response }
      }
    );
    return res.data;
  }
}

export default new ReviewService();